import { useCallback, useMemo } from 'react'

import {
  F0Box,
  SurveyFormBuilder,
  SurveyFormBuilderElement,
  QuestionType,
} from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'

import {
  FromFormToQuestionnaire,
  FromQuestionnaireToForm,
} from 'modules/forms/components/newFormEditor/helpers'
import { usePurchaseRequestDatasets } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm/datasets'
import { QuestionnaireType } from 'modules/forms/components/newFormEditor/types'

const ALLOWED_QUESTION_TYPES: QuestionType[] = [
  'text',
  'longText',
  'select',
  'multi-select',
  'numeric',
  'link',
  'date',
  'file',
  'checkbox',
]

// The SurveyFormBuilder doesn't carry invariantId or element params (fieldIdentifier, scoreType, etc.),
// so we need to keep track of them separately and restore after round-tripping through the builder.
function collectInvariantIds(elements: QuestionnaireType['elements']): Map<string, string> {
  const map = new Map<string, string>()
  for (const el of elements) {
    if (el.id && el.invariantId) map.set(el.id, el.invariantId)
    if ('elements' in el) {
      for (const child of el.elements) {
        if (child.id && child.invariantId) map.set(child.id, child.invariantId)
      }
    }
  }
  return map
}

type ElementParams = QuestionnaireType['elements'][number]['params']

function collectElementParams(elements: QuestionnaireType['elements']): Map<string, ElementParams> {
  const map = new Map<string, ElementParams>()
  for (const el of elements) {
    if (el.id && el.params && Object.keys(el.params).length > 0) map.set(el.id, el.params)
    if ('elements' in el) {
      for (const child of el.elements) {
        if (child.id && child.params && Object.keys(child.params).length > 0)
          map.set(child.id, child.params)
      }
    }
  }
  return map
}

interface Props {
  state: QuestionnaireType
  setState: (state: QuestionnaireType) => void
}

const FormEditor = ({ state, setState }: Props) => {
  const { company } = useCurrentSession()
  const datasets = usePurchaseRequestDatasets(company.id)
  const directUpload = useDirectUpload({ resourceType: 'Procurement::Type' })

  const useUpload = useCallback(
    () => ({
      upload: async (file: File) => {
        const result = await directUpload.upload(file)
        if (result.type === 'aborted') return { type: 'aborted' as const }
        return { type: 'success' as const, value: result.signedId }
      },
      cancelUpload: directUpload.cancelUpload,
      progress: directUpload.progress,
      status: directUpload.status,
    }),
    [directUpload]
  )

  const elements = useMemo(
    () =>
      FromQuestionnaireToForm(state.elements, {
        mapDropdownSingleTo: 'dropdown-single',
      }),
    [state.elements]
  )

  const invariantIds = useMemo(() => collectInvariantIds(state.elements), [state.elements])
  const savedParams = useMemo(() => collectElementParams(state.elements), [state.elements])

  const handleChange = (updatedElements: SurveyFormBuilderElement[]) => {
    const converted = FromFormToQuestionnaire(updatedElements)

    for (const el of converted) {
      el.invariantId = invariantIds.get(el.id ?? '') ?? el.invariantId
      const original = savedParams.get(el.id ?? '')
      if (original) el.params = { ...original, ...el.params }
      if ('elements' in el) {
        for (const child of el.elements) {
          child.invariantId = invariantIds.get(child.id ?? '') ?? child.invariantId
          const childOriginal = savedParams.get(child.id ?? '')
          if (childOriginal) child.params = { ...childOriginal, ...child.params }
        }
      }
    }

    setState({ ...state, elements: converted })
  }

  return (
    <F0Box width="full" height="full" padding="md" paddingBottom="5xl">
      <SurveyFormBuilder
        elements={elements}
        onChange={handleChange}
        allowedQuestionTypes={ALLOWED_QUESTION_TYPES}
        useUpload={useUpload}
        datasets={datasets}
      />
    </F0Box>
  )
}

export default FormEditor
